/**
 * Sales Performance (OE-293): pure arithmetic shared by the snapshot loader, the backend pull and the page.
 * Side-effect free — safe to import from scripts and specs without the @newgenus/common barrel.
 */

/** One IQ STOCTRAN line, reduced to the fields the tool needs. */
export interface SalesLine {
    number: number;
    /** YYYY-MM-DD */
    date: string;
    /** IN invoice, CN credit note, IC internal stock movement (ignored). */
    tcode: string;
    code: string;
    quantity: number;
    /** Excluding VAT, always positive in IQ — the tcode gives the direction. */
    amount: number;
    profit: number;
    debtor: string;
    rep: number;
    /** -1 when the line carries no rep of its own. */
    linerep: number;
}

export interface NetLine {
    date: string;
    code: string;
    debtor: string;
    rep: number;
    amount: number;
    profit: number;
    quantity: number;
}

export interface CategoryTotals {
    sales: number;
    gp: number;
    units: number;
    /** Customers whose net purchases in this category were positive. */
    customers: number;
}

export interface RepMonth {
    sales: number;
    gp: number;
    units: number;
    /** Customers whose net purchases (anything) were positive. */
    activeCustomers: number;
    categories: Record<string, CategoryTotals>;
}

export interface CompanyMonth {
    sales: number;
    gp: number;
    units: number;
    customers: number;
    categories: Record<string, CategoryTotals>;
}

export interface MonthAggregate {
    company: CompanyMonth;
    reps: Record<string, RepMonth>;
}

export type Rag = 'green' | 'amber' | 'red' | 'none';

export const UNCATEGORISED = 'uncategorised';

/** The business month a date falls in: the first month whose end is on or after it (ends sorted ascending). */
export function businessMonthOf(date: string, monthEnds: { key: string; end: string }[]): string | null {
    for (const m of monthEnds) if (date <= m.end) return m.key;
    return null;
}

/** Signs a line by its type and resolves the rep it counts for; null for lines that are not sales. */
export function netLine(l: SalesLine): NetLine | null {
    const sign = l.tcode === 'IN' ? 1 : l.tcode === 'CN' ? -1 : 0;
    if (!sign) return null;
    return {
        date: l.date,
        code: l.code,
        debtor: l.debtor,
        rep: l.linerep >= 0 ? l.linerep : l.rep,
        amount: sign * (l.amount || 0),
        profit: sign * (l.profit || 0),
        quantity: sign * (l.quantity || 0),
    };
}

const round2 = (n: number) => Math.round(n * 100) / 100;
const emptyCat = (): CategoryTotals => ({ sales: 0, gp: 0, units: 0, customers: 0 });

/** Totals one month of lines per rep, per category and for the company. Credit notes net against invoices. */
export function aggregateMonth(lines: SalesLine[], productCategory: Record<string, string>): MonthAggregate {
    // net value per (scope, debtor) decides whether the debtor counts as a buying customer
    const buyer = new Map<string, number>();
    const add = (k: string, v: number) => buyer.set(k, (buyer.get(k) || 0) + v);
    const company: CompanyMonth = { sales: 0, gp: 0, units: 0, customers: 0, categories: {} };
    const reps: Record<string, RepMonth> = {};

    for (const raw of lines) {
        const l = netLine(raw);
        if (!l) continue;
        const cat = productCategory[l.code] || UNCATEGORISED;
        const r = (reps[l.rep] = reps[l.rep] || { sales: 0, gp: 0, units: 0, activeCustomers: 0, categories: {} });
        const rc = (r.categories[cat] = r.categories[cat] || emptyCat());
        const cc = (company.categories[cat] = company.categories[cat] || emptyCat());
        for (const t of [company, r, rc, cc] as { sales: number; gp: number; units: number }[]) {
            t.sales += l.amount;
            t.gp += l.profit;
            t.units += l.quantity;
        }
        add(`c|${l.debtor}`, l.amount);
        add(`r|${l.rep}|${l.debtor}`, l.amount);
        add(`rc|${l.rep}|${cat}|${l.debtor}`, l.amount);
        add(`cc|${cat}|${l.debtor}`, l.amount);
    }

    for (const [k, v] of buyer) {
        if (v <= 0) continue;
        const p = k.split('|');
        if (p[0] === 'c') company.customers++;
        else if (p[0] === 'r') reps[p[1]].activeCustomers++;
        else if (p[0] === 'rc') reps[p[1]].categories[p[2]].customers++;
        else company.categories[p[1]].customers++;
    }

    const tidy = (t: { sales: number; gp: number; units: number }) => {
        t.sales = round2(t.sales);
        t.gp = round2(t.gp);
        t.units = round2(t.units);
    };
    tidy(company);
    Object.values(company.categories).forEach(tidy);
    for (const r of Object.values(reps)) {
        tidy(r);
        Object.values(r.categories).forEach(tidy);
    }
    return { company, reps };
}

/** Share of active customers who bought (0..1). */
export function penetration(customers: number, activeCustomers: number): number {
    return activeCustomers > 0 ? customers / activeCustomers : 0;
}

/**
 * Green: on target, or improved two months running. Red: below half the target, or declined three months
 * running. Amber otherwise. `history` is oldest → newest; the last value is the month being judged.
 */
export function ragStatus(history: number[], target: number | undefined | null): Rag {
    if (!target || !history.length) return 'none';
    const now = history[history.length - 1];
    const run = (n: number, up: boolean) =>
        history.length > n && history.slice(-n - 1).every((v, i, a) => i === 0 || (up ? v > a[i - 1] : v < a[i - 1]));
    if (now >= target || run(2, true)) return 'green';
    if (now < target / 2 || run(3, false)) return 'red';
    return 'amber';
}

/** One purchase date's [sales, gross profit, quantity] — a tuple, because a month holds thousands. */
export type DayTotals = [number, number, number];

export interface CustomerTotals {
    sales: number;
    gp: number;
    units: number;
    /** OE-308: by purchase date (YYYY-MM-DD) */
    days: Record<string, DayTotals>;
    /** per category: what this customer bought in it */
    cats: Record<string, { sales: number; gp: number; units: number; days: Record<string, DayTotals> }>;
}

/**
 * One month's totals per salesperson per customer (OE-306: who bought, who did not), with the same
 * line rules as aggregateMonth: credit notes net against invoices, stock moves left out, the line's
 * rep before the invoice's.
 */
export function customerMonth(
    lines: SalesLine[],
    productCategory: Record<string, string>
): Record<string, Record<string, CustomerTotals>> {
    const out: Record<string, Record<string, CustomerTotals>> = {};
    for (const raw of lines) {
        const l = netLine(raw);
        if (!l) continue;
        const cat = productCategory[l.code] || UNCATEGORISED;
        const rep = (out[l.rep] = out[l.rep] || {});
        const c = (rep[l.debtor] = rep[l.debtor] || { sales: 0, gp: 0, units: 0, days: {}, cats: {} });
        const cc = (c.cats[cat] = c.cats[cat] || { sales: 0, gp: 0, units: 0, days: {} });
        for (const t of [c, cc]) {
            t.sales += l.amount;
            t.gp += l.profit;
            t.units += l.quantity;
            const d = (t.days[l.date] = t.days[l.date] || [0, 0, 0]);
            d[0] += l.amount;
            d[1] += l.profit;
            d[2] += l.quantity;
        }
    }
    for (const rep of Object.values(out))
        for (const c of Object.values(rep)) {
            for (const t of [c, ...Object.values(c.cats)]) {
                t.sales = round2(t.sales);
                t.gp = round2(t.gp);
                t.units = round2(t.units);
                for (const d of Object.values(t.days)) for (let i = 0; i < 3; i++) d[i] = round2(d[i]);
            }
        }
    return out;
}
